import { buildPhaseTransitionSafetySnapshot } from './riskProfileFlags';
import { sha256Hex } from './journeyState';
import { canonicalJsonbText } from './canonicalJsonb';

const MAX_SUMMARY_LENGTH = 1600;
const MAX_QUESTION_LENGTH = 320;
const MAX_EVIDENCE_LENGTH = 360;
const MAX_ANSWER_LENGTH = 2400;
const MAX_QUESTIONS = 7;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CYCLE_REVIEW_TEMPLATE_VALUES = new Set(['adherence', 'results', 'metabolic', 'final']);
const ASCII_WHITESPACE_RE = /[ \t\n\r\f\v]+/g;
const ASCII_EDGE_WHITESPACE_RE = /^[ \t\n\r\f\v]+|[ \t\n\r\f\v]+$/g;

const isPlainObject = (value) => (
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
);

const canonicalSingleLineText = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(ASCII_WHITESPACE_RE, ' ').replace(/^ +| +$/g, '');
};
const unicodeLength = (value) => Array.from(value).length;
const boundedText = (value, maxLength) => (
  Array.from(canonicalSingleLineText(value)).slice(0, maxLength).join('')
);

function normalizeCycleReviewProof(value) {
  if (!isPlainObject(value)) {
    throw new Error('Bilan de cycle non lié au rendez-vous — à valider Anissa.');
  }
  const keys = Object.keys(value);
  if (
    keys.length !== 3
    || keys.some((key) => !['id', 'step_number', 'template_type'].includes(key))
    || !UUID_RE.test(value.id)
    || !Number.isInteger(value.step_number)
    || value.step_number < 1
    || value.step_number > 8
    || typeof value.template_type !== 'string'
    || !CYCLE_REVIEW_TEMPLATE_VALUES.has(value.template_type)
  ) {
    throw new Error('Bilan de cycle non canonique — à valider Anissa.');
  }
  return {
    id: value.id.toLowerCase(),
    step_number: value.step_number,
    template_type: value.template_type,
  };
}

export const normalizeMeetingPractitionerNote = (value) => (
  typeof value === 'string'
    ? value.replace(/\r\n/g, '\n').replace(ASCII_EDGE_WHITESPACE_RE, '')
    : ''
);

const QUESTION_TEMPLATES = Object.freeze({
  clarify: (fact) => `Que souhaites-tu préciser à propos de ce point : « ${fact.label} » ?`,
  context: (fact) => `Dans quelles situations remarques-tu ce point : « ${fact.label} » ?`,
  daily_experience: (fact) => `Comment vis-tu ce point au quotidien : « ${fact.label} » ?`,
});

const FACT_REF = /^[a-z][a-z0-9:_-]{0,79}$/;

function normalizeFactCatalog(facts) {
  if (!Array.isArray(facts) || facts.length === 0 || facts.length > 10) {
    throw new Error('Le catalogue factuel du rendez-vous est indisponible.');
  }
  const seen = new Set();
  return facts.map((fact, index) => {
    if (!isPlainObject(fact)) throw new Error(`Le fait ${index + 1} est invalide.`);
    const ref = typeof fact.ref === 'string' ? fact.ref.trim() : '';
    const label = boundedText(fact.label, MAX_EVIDENCE_LENGTH);
    if (!FACT_REF.test(ref) || !label || seen.has(ref)) {
      throw new Error(`Le fait ${index + 1} est invalide.`);
    }
    seen.add(ref);
    return { ref, label };
  });
}

/**
 * L'IA ne peut renvoyer AUCUN texte libre : seulement une référence factuelle
 * locale et une intention conversationnelle fermée. Le rendu humain est
 * déterministe et reste décoché jusqu'au choix explicite d'Anissa.
 */
export function normalizeGeneratedMeetingBriefing(value, facts) {
  if (!isPlainObject(value)) {
    throw new Error("La proposition n’a pas le format attendu.");
  }
  const rootKeys = Object.keys(value);
  if (rootKeys.length !== 1 || rootKeys[0] !== 'questions' || !Array.isArray(value.questions)) {
    throw new Error('La proposition contient des champs non autorisés.');
  }

  const factCatalog = normalizeFactCatalog(facts);
  const factsByRef = new Map(factCatalog.map((fact) => [fact.ref, fact]));
  const summary = boundedText(
    `Éléments factuels disponibles : ${factCatalog.map((fact) => fact.label).join(' ; ')}.`,
    MAX_SUMMARY_LENGTH,
  );
  const inputQuestions = value.questions;
  if (inputQuestions.length > MAX_QUESTIONS) {
    throw new Error(`La proposition dépasse ${MAX_QUESTIONS} questions.`);
  }

  const seenQuestions = new Set();
  const questions = inputQuestions.map((item, index) => {
    if (!isPlainObject(item)) {
      throw new Error(`La question ${index + 1} n'a pas le format attendu.`);
    }
    const itemKeys = Object.keys(item);
    if (
      itemKeys.length !== 2
      || !Object.hasOwn(item, 'fact_ref')
      || !Object.hasOwn(item, 'intent')
      || itemKeys.some((key) => key !== 'fact_ref' && key !== 'intent')
    ) {
      throw new Error(`La question ${index + 1} contient des champs non autorisés.`);
    }
    const fact = factsByRef.get(item.fact_ref);
    const ownsIntent = typeof item.intent === 'string' && Object.hasOwn(QUESTION_TEMPLATES, item.intent);
    const renderQuestion = ownsIntent ? QUESTION_TEMPLATES[item.intent] : null;
    const questionKey = `${item.fact_ref}\u0000${item.intent}`;
    if (!fact || typeof renderQuestion !== 'function' || seenQuestions.has(questionKey)) {
      throw new Error(`La question ${index + 1} référence un fait ou une intention non autorisé.`);
    }
    const renderedText = boundedText(renderQuestion(fact), MAX_QUESTION_LENGTH);
    if (!renderedText) {
      throw new Error(`La question ${index + 1} n'a pas pu être rendue.`);
    }
    seenQuestions.add(questionKey);
    return {
      id: `ia-${index + 1}`,
      text: renderedText,
      evidence: fact.label,
      source: 'ia',
      included: false,
      answer: '',
    };
  });

  return { summary, questions };
}

export function createManualMeetingQuestion(text, id = null) {
  const normalized = boundedText(text, MAX_QUESTION_LENGTH);
  if (!normalized) return null;
  return {
    id: id || `anissa-${crypto.randomUUID()}`,
    text: normalized,
    evidence: 'Ajoutée manuellement par Anissa.',
    source: 'anissa',
    included: true,
    answer: '',
  };
}

export function assertMeetingAiSafetySnapshot(safetySnapshot) {
  let normalized;
  try {
    normalized = buildPhaseTransitionSafetySnapshot(safetySnapshot);
  } catch (error) {
    throw new Error(`À valider Anissa — ${error?.message || 'complète le contexte de sécurité.'}`);
  }
  const maternalContextPresent = [
    normalized.grossesseActuelle,
    normalized.allaitement,
    normalized.postPartum,
  ].some((value) => value === 'Oui');
  if (maternalContextPresent || normalized.flagsProfilRisque.length > 0) {
    throw new Error('Contexte maternel ou profil à risque — à valider Anissa. Prépare ce rendez-vous manuellement.');
  }
  return normalized;
}

export function isMeetingAiSnapshotReady(syncStatus, loading, safetySnapshot) {
  if (syncStatus !== 'ok' || loading === true) return false;
  try {
    assertMeetingAiSafetySnapshot(safetySnapshot);
    return true;
  } catch {
    return false;
  }
}

export function invalidateGeneratedMeetingWorkspace({ summary = '', summarySource = 'manual', questions = [] } = {}) {
  return {
    summary: summarySource === 'ia' ? '' : boundedText(summary, MAX_SUMMARY_LENGTH),
    summarySource: 'manual',
    questions: (Array.isArray(questions) ? questions : [])
      .filter((question) => question?.source === 'anissa')
      .slice(0, MAX_QUESTIONS),
  };
}

/**
 * Construit le fragment persistant du compte rendu. Les questions non retenues
 * ne sont jamais enregistrées. Les réponses restent des notes internes Anissa.
 */
function buildMeetingRecordContent({
  clientId = '',
  sourceConsultationId = '',
  consultationOrdinal = null,
  summary = '',
  periodLabel = '',
  practitionerNote = '',
  questions = [],
  cycleReview = null,
  occurredConfirmed = false,
} = {}) {
  if (!UUID_RE.test(clientId) || !UUID_RE.test(sourceConsultationId)) {
    throw new Error('Compte rendu non lié au dossier et au plan source — à valider Anissa.');
  }
  if (!Number.isInteger(consultationOrdinal) || consultationOrdinal < 1 || consultationOrdinal > 6) {
    throw new Error('Ordinal du rendez-vous invalide — à valider Anissa.');
  }
  const safeSummary = canonicalSingleLineText(summary);
  if (unicodeLength(safeSummary) > MAX_SUMMARY_LENGTH) {
    throw new Error(`La synthèse dépasse ${MAX_SUMMARY_LENGTH} caractères.`);
  }
  const safePeriod = canonicalSingleLineText(periodLabel);
  if (unicodeLength(safePeriod) > 160) {
    throw new Error('La période du rendez-vous est trop longue — à valider Anissa.');
  }
  if (typeof practitionerNote !== 'string') {
    throw new Error("La note d'Anissa est invalide — à vérifier.");
  }
  const safePractitionerNote = normalizeMeetingPractitionerNote(practitionerNote);
  if (unicodeLength(safePractitionerNote) > 2400) {
    throw new Error("La note d'Anissa dépasse 2400 caractères.");
  }
  const includedQuestions = (Array.isArray(questions) ? questions : [])
    .filter((question) => question?.included === true);
  if (includedQuestions.length > MAX_QUESTIONS) {
    throw new Error(`Le compte rendu dépasse ${MAX_QUESTIONS} questions — à valider Anissa.`);
  }
  const safeQuestions = includedQuestions
    .map((question, index) => {
      const answer = canonicalSingleLineText(question?.answer);
      if ((typeof question?.answer === 'string' && unicodeLength(question.answer) > MAX_ANSWER_LENGTH)
          || unicodeLength(answer) > MAX_ANSWER_LENGTH) {
        throw new Error(`La réponse ${index + 1} dépasse ${MAX_ANSWER_LENGTH} caractères.`);
      }
      const text = canonicalSingleLineText(question?.text);
      if (typeof question?.text !== 'string' || unicodeLength(text) > MAX_QUESTION_LENGTH) {
        throw new Error(`La question ${index + 1} est invalide — à valider Anissa.`);
      }
      const id = canonicalSingleLineText(question?.id);
      if (question?.id != null && (typeof question.id !== 'string' || unicodeLength(id) > 100)) {
        throw new Error(`L'identifiant de la question ${index + 1} est invalide — à valider Anissa.`);
      }
      if (!text) return null;
      return {
        id: id || `question-${index + 1}`,
        text,
        source: question?.source === 'anissa' ? 'anissa' : 'ia',
        answer,
      };
    })
    .filter(Boolean);

  if (!safeSummary && safeQuestions.length === 0) return null;
  const content = {
    version: 2,
    client_id: clientId,
    source_consultation_id: sourceConsultationId,
    consultation_ordinal: consultationOrdinal,
    summary: safeSummary,
    period_label: safePeriod,
    practitioner_note: safePractitionerNote,
    questions: safeQuestions,
  };
  if (cycleReview !== null || occurredConfirmed === true) {
    if (occurredConfirmed !== true) return null;
    content.occurred_confirmed = true;
    content.cycle_review = normalizeCycleReviewProof(cycleReview);
  }
  return content;
}

const REVIEWED_RECORD_KEYS = new Set([
  'version',
  'client_id',
  'source_consultation_id',
  'consultation_ordinal',
  'summary',
  'period_label',
  'practitioner_note',
  'questions',
  'reviewed_by_anissa',
  'review_content_sha256',
]);
const REVIEWED_CYCLE_RECORD_KEYS = new Set([
  ...REVIEWED_RECORD_KEYS,
  'occurred_confirmed',
  'cycle_review',
]);
const REVIEWED_QUESTION_KEYS = new Set(['id', 'text', 'source', 'answer']);

/**
 * Validation de confiance du compte rendu reçu par le service d'écriture.
 * La preuve est liée au contenu borné exact, pas au simple booléen envoyé par
 * l'interface. Toute clé enrichie, mutation ou confirmation absente ferme le
 * flux avant l'écriture du journal.
 */
export async function validateReviewedMeetingRecord(value) {
  const hasCycleProof = isPlainObject(value)
    && (Object.hasOwn(value, 'occurred_confirmed') || Object.hasOwn(value, 'cycle_review'));
  const allowedKeys = hasCycleProof ? REVIEWED_CYCLE_RECORD_KEYS : REVIEWED_RECORD_KEYS;
  if (
    !isPlainObject(value)
    || Object.keys(value).length !== allowedKeys.size
    || Object.keys(value).some((key) => !allowedKeys.has(key))
  ) {
    throw new Error('Compte rendu de rendez-vous absent ou invalide — à valider Anissa.');
  }
  if (
    value.version !== 2
    || !UUID_RE.test(value.client_id)
    || !UUID_RE.test(value.source_consultation_id)
    || !Number.isInteger(value.consultation_ordinal)
    || value.consultation_ordinal < 1
    || value.consultation_ordinal > 6
    || value.reviewed_by_anissa !== true
    || typeof value.review_content_sha256 !== 'string'
    || !/^[a-f0-9]{64}$/.test(value.review_content_sha256)
    || typeof value.summary !== 'string'
    || unicodeLength(value.summary) > MAX_SUMMARY_LENGTH
    || value.summary !== boundedText(value.summary, MAX_SUMMARY_LENGTH)
    || typeof value.period_label !== 'string'
    || unicodeLength(value.period_label) > 160
    || value.period_label !== boundedText(value.period_label, 160)
    || typeof value.practitioner_note !== 'string'
    || unicodeLength(value.practitioner_note) > 2400
    || value.practitioner_note !== normalizeMeetingPractitionerNote(value.practitioner_note)
    || !Array.isArray(value.questions)
    || value.questions.length > MAX_QUESTIONS
  ) {
    throw new Error('Compte rendu de rendez-vous non confirmé — à valider Anissa.');
  }

  const questions = value.questions.map((question) => {
    if (
      !isPlainObject(question)
      || Object.keys(question).length !== REVIEWED_QUESTION_KEYS.size
      || Object.keys(question).some((key) => !REVIEWED_QUESTION_KEYS.has(key))
      || typeof question.id !== 'string'
      || unicodeLength(question.id) > 100
      || question.id !== boundedText(question.id, 100)
      || typeof question.text !== 'string'
      || !boundedText(question.text, MAX_QUESTION_LENGTH)
      || unicodeLength(question.text) > MAX_QUESTION_LENGTH
      || question.text !== boundedText(question.text, MAX_QUESTION_LENGTH)
      || (question.source !== 'ia' && question.source !== 'anissa')
      || typeof question.answer !== 'string'
      || unicodeLength(question.answer) > MAX_ANSWER_LENGTH
      || question.answer !== boundedText(question.answer, MAX_ANSWER_LENGTH)
    ) {
      throw new Error('Compte rendu de rendez-vous modifié — à valider Anissa.');
    }
    return { ...question, included: true };
  });
  const content = buildMeetingRecordContent({
    clientId: value.client_id,
    sourceConsultationId: value.source_consultation_id,
    consultationOrdinal: value.consultation_ordinal,
    summary: value.summary,
    periodLabel: value.period_label,
    practitionerNote: value.practitioner_note,
    questions,
    cycleReview: hasCycleProof ? value.cycle_review : null,
    occurredConfirmed: hasCycleProof ? value.occurred_confirmed : false,
  });
  if (!content) {
    throw new Error('Compte rendu de rendez-vous vide — à valider Anissa.');
  }
  const fingerprint = canonicalJsonbText(content);
  const expectedSha = await sha256Hex(fingerprint);
  if (expectedSha !== value.review_content_sha256) {
    throw new Error('Compte rendu de rendez-vous modifié après validation — à valider Anissa.');
  }
  return Object.freeze({
    ...content,
    reviewed_by_anissa: true,
    review_content_sha256: expectedSha,
  });
}

export function buildMeetingReviewFingerprint(input = {}) {
  const content = buildMeetingRecordContent(input);
  return content ? canonicalJsonbText(content) : null;
}

export function buildMeetingRecord(input = {}) {
  const content = buildMeetingRecordContent(input);
  if (!content) return null;
  return {
    ...content,
    reviewed_by_anissa: false,
  };
}

/**
 * Porte UI commune entre la préparation et le compte rendu. Une consultation
 * ne peut pas avancer avec un écran apparemment rempli mais sans aucune trace
 * factuelle persistable. La note praticienne reste optionnelle et ne remplace
 * pas la synthèse ou une question retenue.
 */
export function getMeetingWorkspaceReadiness({ summary = '', questions = [] } = {}) {
  const hasSummary = canonicalSingleLineText(summary).length > 0;
  const hasIncludedQuestion = (Array.isArray(questions) ? questions : [])
    .some((question) => (
      question?.included === true
      && canonicalSingleLineText(question?.text).length > 0
    ));
  const ready = hasSummary || hasIncludedQuestion;
  return Object.freeze({
    ready,
    hasSummary,
    hasIncludedQuestion,
    issue: ready
      ? null
      : 'Ajoute une synthèse ou retiens au moins une question avant de passer au compte rendu.',
  });
}

export async function buildReviewedMeetingRecord({ reviewConfirmation = null, ...input } = {}) {
  const content = buildMeetingRecordContent(input);
  if (!content) return null;
  const contentFingerprint = canonicalJsonbText(content);
  if (!reviewConfirmation || reviewConfirmation !== contentFingerprint) {
    throw new Error('À valider Anissa — relis et confirme le compte rendu exact avant de l’enregistrer.');
  }
  return {
    ...content,
    reviewed_by_anissa: true,
    review_content_sha256: await sha256Hex(contentFingerprint),
  };
}

const CLINICAL_KEYS = new Set(['appointment', 'workflow_action']);
const WORKFLOW_ACTION_VALUES = new Set(['continue', 'adapt', 'transition', 'newVersion']);

export async function validateReviewedClinicalRecord(value, expected = {}) {
  if (!isPlainObject(value) || !value.appointment) {
    throw new Error('Compte rendu de rendez-vous absent — à valider Anissa.');
  }
  if (
    Object.keys(value).length !== CLINICAL_KEYS.size
    || Object.keys(value).some((key) => !CLINICAL_KEYS.has(key))
  ) {
    throw new Error('Suivi clinique incomplet ou enrichi par un champ inconnu — à valider Anissa.');
  }
  if (!WORKFLOW_ACTION_VALUES.has(value.workflow_action)) {
    throw new Error('Action de workflow inconnue — à valider Anissa.');
  }
  const appointment = await validateReviewedMeetingRecord(value.appointment);
  const expectedCycleReview = expected.cycleReview == null
    ? null
    : normalizeCycleReviewProof(expected.cycleReview);
  if (
    (expected.clientId && appointment.client_id !== expected.clientId)
    || (expected.sourceConsultationId && appointment.source_consultation_id !== expected.sourceConsultationId)
    || (expected.consultationOrdinal && appointment.consultation_ordinal !== expected.consultationOrdinal)
    || (expectedCycleReview && (
      appointment.occurred_confirmed !== true
      || !isPlainObject(appointment.cycle_review)
      || appointment.cycle_review.id !== expectedCycleReview.id
      || appointment.cycle_review.step_number !== expectedCycleReview.step_number
      || appointment.cycle_review.template_type !== expectedCycleReview.template_type
    ))
  ) {
    throw new Error('Le compte rendu ne correspond plus à ce dossier, ce plan ou ce rendez-vous — à valider Anissa.');
  }
  return Object.freeze({
    appointment,
    workflow_action: value.workflow_action,
  });
}

export const MEETING_WORKSPACE_LIMITS = Object.freeze({
  maxQuestions: MAX_QUESTIONS,
  maxQuestionLength: MAX_QUESTION_LENGTH,
  maxAnswerLength: MAX_ANSWER_LENGTH,
});
